/* ═══════════════════════════════════════════════════════════════
   SYNC — planReview: apply the ACCEPTED changes to the copy
   (docs/SYNC_PLAN.md §2.1). Pure — returns the new block list; the
   client writes `ReviewPlan.blocks` into Yjs elsewhere.

   Order of work, so every offset and anchor stays valid:
     1. whole 'changed' (formatting / structure / image)
     2. word runs, per block / cell, right-to-left
     3. placements — moves + adds, anchors before dependants (chained per anchor)
     4. removals
   Undecided = untouched. 'reconciled' items carry no decision.
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock, TableData, TextRunSpan } from '../spec';
import { CONTENT_KEYS } from '../spec-compose';
import { shapeOf } from './merge3';
import { spliceWords, tokenize, untokenize } from './tokens';
import type { Change, Decision, ReviewPlan } from './types';

const SMART_KEYS = ['smartBlockId', 'smartBlockKind', 'smartParams'] as const;

function cloneTable(td: TableData): TableData {
  return { ...td, cells: td.cells.map((r) => r.map((c) => ({ ...c }))) };
}

/** Copy upstream's content onto the copy's block (its id, stamps, comments
 *  and other non-content fields stay). Words stay the copy's unless the
 *  block's kind or table shape changed, or it is an image / smart token. */
/** True when accepting `up` replaces the block's words too (image, smart
 *  token, a kind change, or a table reshaped) — word items are then moot. */
function replacesWords(cur: ContentBlock, up: ContentBlock): boolean {
  return (
    up.blockType === 'image' ||
    up.blockType === 'smart-token' ||
    cur.blockType === 'image' ||
    cur.blockType === 'smart-token' ||
    (up.blockType || 'text') !== (cur.blockType || 'text') ||
    shapeOf(up.tableData) !== shapeOf(cur.tableData)
  );
}

function applyWholeChange(cur: ContentBlock, up: ContentBlock): ContentBlock {
  const next = { ...cur } as unknown as Record<string, unknown>;
  const src = up as unknown as Record<string, unknown>;
  const wholeOnly = replacesWords(cur, up);
  for (const key of CONTENT_KEYS) {
    if (!wholeOnly && (key === 'text' || key === 'runs')) continue;
    if (key === 'tableData' && !wholeOnly && up.tableData && cur.tableData) {
      const mine = cur.tableData.cells;
      next.tableData = {
        ...cloneTable(up.tableData),
        cells: up.tableData.cells.map((r, ri) =>
          r.map((c, ci) => {
            const { text: _t, runs: _r, ...rest } = c;
            const own = mine[ri][ci];
            return { ...rest, text: own.text, ...(own.runs ? { runs: own.runs } : {}) };
          })
        ),
      };
      continue;
    }
    if (src[key] === undefined) delete next[key];
    else next[key] = src[key];
  }
  if (wholeOnly) {
    for (const key of SMART_KEYS) {
      if (src[key] === undefined) delete next[key];
      else next[key] = src[key];
    }
  }
  // Identity survives (Sync D1/D5): the copy's id always; its module stamp
  // unless upstream carries one (then upstream's — it describes the content
  // just taken). An upstream that lacks a stamp never strips the copy's, or
  // the next module publish would skip this copy.
  next.id = cur.id;
  if (src.moduleStamp !== undefined) next.moduleStamp = src.moduleStamp;
  return next as unknown as ContentBlock;
}

/** Apply word-run changes to one text, right-to-left. */
function applyRuns(text: string, changes: Change[]): string {
  const ordered = changes
    .map((c, i) => ({ c, i }))
    .sort(
      (x, y) =>
        (y.c.range?.start ?? 0) - (x.c.range?.start ?? 0) ||
        (y.c.range?.end ?? 0) - (x.c.range?.end ?? 0) ||
        y.i - x.i
    );
  let tok = tokenize(text);
  for (const { c } of ordered) {
    const r = c.range;
    if (!r) continue;
    const n = tok.words.length;
    const start = Math.min(Math.max(0, r.start), n);
    const end = Math.min(Math.max(start, r.end), n);
    tok = spliceWords(tok, start, end, tokenize(c.after));
  }
  return untokenize(tok);
}

/** Text + emphasis after a word edit. When the words now match upstream's
 *  word for word, upstream's exact text and runs land (runs must concatenate
 *  to the text, so spacing follows upstream). Unchanged words keep the
 *  copy's own runs; anything else drops stale runs. */
function landing(
  finalText: string,
  original: string,
  ownRuns: TextRunSpan[] | undefined,
  upText: string | undefined,
  upRuns: TextRunSpan[] | undefined
): { text: string; runs?: TextRunSpan[] } {
  if (upText !== undefined && sameWords(tokenize(finalText).words, tokenize(upText).words)) {
    return { text: upText, ...(upRuns ? { runs: upRuns } : {}) };
  }
  if (finalText === original) return { text: finalText, ...(ownRuns ? { runs: ownRuns } : {}) };
  return { text: finalText };
}

const sameWords = (x: string[], y: string[]): boolean =>
  x.length === y.length && x.every((w, i) => w === y[i]);

export function planReview(
  ours: ContentBlock[],
  changes: Change[],
  decisions: Record<string, Decision>
): ReviewPlan {
  let blocks: ContentBlock[] = (ours || []).map((b) => ({ ...b }));
  let applied = 0;
  let rejected = 0;
  const accepted: Change[] = [];
  for (const c of changes || []) {
    if (c.kind === 'reconciled') continue;
    const d = decisions[c.id];
    if (d === 'reject') rejected++;
    else if (d === 'accept') accepted.push(c);
  }
  const indexOf = (id: string): number => blocks.findIndex((b) => b.id === id);

  // 1. Whole-block content changes.
  const wordsReplaced = new Set<string>();
  for (const c of accepted) {
    if (c.kind !== 'changed' || !c.whole || !c.block) continue;
    const i = indexOf(c.blockId);
    if (i < 0) continue;
    if (replacesWords(blocks[i], c.block)) wordsReplaced.add(c.blockId);
    blocks[i] = applyWholeChange(blocks[i], c.block);
    applied++;
  }

  // 2. Word runs, grouped per block / cell.
  const groups = new Map<string, Change[]>();
  for (const c of accepted) {
    if (c.whole || !c.range || wordsReplaced.has(c.blockId)) continue;
    const k = c.cell ? `${c.blockId}\u0000${c.cell.row}\u0000${c.cell.col}` : c.blockId;
    const list = groups.get(k);
    if (list) list.push(c);
    else groups.set(k, [c]);
  }
  for (const list of groups.values()) {
    const first = list[0];
    const i = indexOf(first.blockId);
    if (i < 0) continue;
    const cur = blocks[i];
    const up = list.find((c) => c.block)?.block;
    if (first.cell) {
      const { row, col } = first.cell;
      const cellNow = cur.tableData?.cells[row]?.[col];
      if (!cur.tableData || !cellNow) continue;
      const td = cloneTable(cur.tableData);
      const finalText = applyRuns(cellNow.text, list);
      const upCell = up?.tableData?.cells[row]?.[col];
      const land = landing(finalText, cellNow.text, cellNow.runs, upCell?.text, upCell?.runs);
      const { runs: _drop, ...rest } = cellNow;
      td.cells[row][col] = { ...rest, ...land };
      blocks[i] = { ...cur, tableData: td };
    } else {
      const finalText = applyRuns(cur.text, list);
      const land = landing(finalText, cur.text, cur.runs, up?.text, up?.runs);
      const { runs: _drop, ...rest } = cur;
      blocks[i] = { ...rest, ...land };
    }
    applied += list.length;
  }

  // 3. Placements — moves and adds in ONE pass, in the engine's order
  //    (same anchor ⇒ upstream order), chained per anchor. A placement whose
  //    anchor is itself an accepted move waits until that block has landed,
  //    so every block lands after its upstream predecessor.
  const queue = accepted.filter((c) => c.whole && (c.kind === 'moved' || c.kind === 'added'));
  const pendingMoves = new Set(queue.filter((c) => c.kind === 'moved').map((c) => c.blockId));
  const lastPlaced = new Map<string, string>();
  while (queue.length) {
    let qi = queue.findIndex((c) => {
      const a = c.anchor?.after;
      return !a || a === c.blockId || !pendingMoves.has(a);
    });
    if (qi < 0) qi = 0;
    const c = queue.splice(qi, 1)[0];
    pendingMoves.delete(c.blockId);
    let block: ContentBlock;
    if (c.kind === 'moved') {
      const i = indexOf(c.blockId);
      if (i < 0) continue;
      block = blocks[i];
      blocks = blocks.slice(0, i).concat(blocks.slice(i + 1));
    } else {
      if (!c.block || indexOf(c.blockId) >= 0) continue;
      block = { ...c.block };
    }
    const anchorKey = c.anchor?.after ?? '@@start';
    const chainedAfter = lastPlaced.get(anchorKey);
    let at: number;
    if (chainedAfter !== undefined && indexOf(chainedAfter) >= 0) {
      at = indexOf(chainedAfter) + 1;
    } else if (c.anchor?.after) {
      const ai = indexOf(c.anchor.after);
      at = ai >= 0 ? ai + 1 : blocks.length;
    } else {
      at = 0;
    }
    blocks.splice(at, 0, block);
    lastPlaced.set(anchorKey, block.id);
    applied++;
  }

  // 4. Removals.
  const removeIds = new Set<string>();
  for (const c of accepted) {
    if (c.whole && c.kind === 'removed' && indexOf(c.blockId) >= 0 && !removeIds.has(c.blockId)) {
      removeIds.add(c.blockId);
      applied++;
    }
  }
  if (removeIds.size) blocks = blocks.filter((b) => !removeIds.has(b.id));

  return { blocks, applied, rejected };
}
